import "./styles.css";
import { FC, useState, useContext } from "react";
import { useFormik } from "formik";
import { Box, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import useMutation from "../../../../../shared/components/hooks/useMutation";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import { SidebarContext } from "../../../../../contexts/SidebarContext";
import InputTextfield from "../../../../../shared/components/textField";
import { ZSAutocomplete } from "../../../../../shared/components/autoComplete";
import { ZSSelectBox } from "../../../../../shared/components/selectBox";
import { FormContainer } from "../../../../../shared/components/formContainer";
import { ViewContainer } from "../../../../../shared/components/viewContainer";
import SingleEntityContainer from "../../../../../shared/components/singleEntityContainer";
import { TitleBarProps } from "../../../../../shared/components/titleBar";
import { getTextFields } from "../variables/textFields";
import { genderOptions } from "../../../../../shared/constants/selectOptions";
import { Role } from "../../../../../shared/interfaces/role";
import { customizeValidationSchema } from "../variables/customSchema";
import { User, UserRole } from "../../../../../shared/interfaces/user";
import { EditButton } from "../../../../../shared/components/editButton";
import { CloseButton } from "../../../../../shared/components/closeButton";
import { ProfileBorderBox } from "../../../../../shared/components/profileComponents";

const UserEditView: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { loading, data, fetchData } = useQuery(`/admin/user/${id}`, null);
  const { payload, setPayload } = useContext(SidebarContext);
  const { loading: apiLoading, modifyData } = useMutation();
  const [editStatus, setEditStatus] = useState(false);

  //Fetching and formatting role data
  const { data: rolesData } = useQuery(
    editStatus ? "/roles" : "",
    editStatus
      ? {
          params: {
            ...payload,
            take: 25,
            sortOrder: "ASC",
            enabled: true,
          },
        }
      : null
  );

  const fetchedRoles: Role[] = (rolesData as any)?.items || [];
  const roles = fetchedRoles?.map((role: Role) => ({
    value: role.id,
    label: role.name,
  }));
  //#End of Fetching and formatting role data

  const userData: User = (data as any)?.data || {};
  const selectedRoleData: UserRole[] =
    userData?.userRole?.map((data: any) => ({
      id: data.id,
      role: {
        value: data.role.id,
        label: data.role.name,
      },
    })) || [];

  //Edit permission checking
  const editPermission = (data as any)?.entityPermissions
    ? (data as any)?.entityPermissions?.includes("User-Update")
    : true;
  //End of edit permission checking


  const adminEditTooltipNode = (
    <>
      {editPermission && <EditButton onClick={() => setEditStatus(true)} />}
      <CloseButton onClick={() => closeDetailsView()} />
    </>
  );

  const initialValues = {
    name: userData?.admin?.name,
    phone: userData?.phone,
    gender: userData?.admin?.gender,
    email: userData?.email,
    userRoleID: selectedRoleData[0]?.id || "",
    roleID: selectedRoleData[0]?.role?.value || "",
  };

  const userForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,

    validationSchema: customizeValidationSchema(editStatus),

    onSubmit: async (formData: any) => {
      let response;
      if (id) {
        response = await modifyData(
          `/admin/user/${id}`,
          "put",
          {
            ...formData,
          },
          userForm
        );
      }
      if (response) {
        fetchData();
        closeUserEdit();
      }
    },
  });

  const closeUserEdit = () => {
    fetchData();
    setEditStatus(false);
  };

  const editTitlebar: TitleBarProps = {
    headerTitle: "User -  Edit",
    title: "Edit User",
    buttonTitle: "Save",
    onClick: () => userForm.handleSubmit(),
    dirty: userForm.dirty,
    buttonLoading: apiLoading,
    editCloseIcon: {
      visible: true,
      onClick: () => closeUserEdit(),
    },
  };

  const viewTitleBar: TitleBarProps = {
    title: "User Details",
    headerTitle: "User Details",
  };
  const userEditFields = getTextFields(userData);

  const closeDetailsView = () => {
    navigate("/user-controls/users");
  };

  return (
    <>
      {editStatus && (
        <>
          <ProfileBorderBox>
            <FormContainer titleBar={editTitlebar}>
              <Box className="form-border-custom">
                <Grid
                  container
                  alignItems="stretch"
                  columnSpacing={4}
                  rowSpacing={2}
                >
                  {userEditFields.map(
                    (field: any, index: number) =>
                      (field?.formField === undefined ||
                        field.formField === true) && (
                        <Grid item md={6} sm={6} xs={12} key={field.id}>
                          <InputTextfield
                            form={userForm}
                            fieldProps={field}
                            key={index}
                          />
                        </Grid>
                      )
                  )}
                  <Grid item md={6} sm={6} xs={12} key="role">
                    <ZSAutocomplete
                      placeholder="Select role"
                      options={roles}
                      label="Role"
                      required
                      setValue={(selectedOption: any) => {
                        userForm.setFieldTouched("roleID", true);
                        if (selectedOption?.value) {
                          userForm.setFieldValue(
                            "roleID",
                            selectedOption?.value
                          );
                        } else {
                          userForm.setFieldValue("roleID", "");
                        }
                      }}
                      defaultValue={selectedRoleData[0]?.role}
                      setBlur={() => {
                        userForm.setFieldTouched("roleID", true);
                      }}
                      refetchFunction={(searchString: string) => {
                        setPayload((prevData: any) => {
                          return {
                            ...prevData,
                            search: searchString,
                          };
                        });
                      }}
                      form={{ ...userForm, name: "roleID" }}
                    />
                  </Grid>
                  <Grid item md={6} sm={6} xs={12} key="gender">
                    <ZSSelectBox
                      fieldProps={{
                        id: "admin-gender",
                        name: "gender",
                        placeholder: "Select gender",
                        label: "Gender",
                        required: true,
                      }}
                      options={genderOptions}
                      form={userForm}
                      value={userForm.values?.gender}
                    />
                  </Grid>
                </Grid>
              </Box>
            </FormContainer>
          </ProfileBorderBox>
        </>
      )}

      {!editStatus && (
        <ProfileBorderBox>
          <ViewContainer loading={loading} titleBar={viewTitleBar}>
            <div className="entity-container">
              <SingleEntityContainer
                entityFields={userEditFields}
                tooltips={adminEditTooltipNode}
              ></SingleEntityContainer>
            </div>
          </ViewContainer>
        </ProfileBorderBox>
      )}
    </>
  );
};

export default UserEditView;
